/**
 * voice-v8 — Werkzeugkarten: Reise: Flüge, Unterkünfte, Live-Übersetzung.
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3) — Verhalten unverändert;
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import { absatz, aktionsKnopf, aktionsLeiste, baueKarte, kartenTitel, leerHinweis, wertZeile } from "../karte.js";
import { proxiedImg, unwrapResult } from "./hilfe.js";

/**
 * 2026-07-28 (Martin: "er soll mit Links mit anbieten wenn Booking kommt und
 * am besten mit Fotos — mache bitte ein Modul daraus"): Unterkuenfte als
 * anklickbare Karten statt als vorgelesene Aufzaehlung.
 *
 * Quellen-neutral: Die Karte weiss nichts von Booking, Airbnb oder sonst
 * einer Plattform — sie stellt dar, was ihr uebergeben wird. Aendert eine
 * Seite ihren Aufbau, betrifft das die Recherche, nicht die Darstellung.
 */
interface UnterkunftItem {
  name?: string;
  url?: string;
  bild_url?: string;
  bewertung?: number;
  bewertungen_anzahl?: number;
  preis?: string;
  entfernung?: string;
  merkmale?: string[];
  hinweis?: string;
  empfehlung?: boolean;
}

export function buildUnterkunftCard(result: unknown): HTMLElement {
  const r = unwrapResult(result) as {
    ok?: boolean;
    error?: string;
    titel?: string;
    unterkuenfte?: UnterkunftItem[];
  };
  const el = baueKarte("unterkunft");
  el.appendChild(kartenTitel(r.titel || "Unterkünfte"));
  const liste = Array.isArray(r.unterkuenfte) ? r.unterkuenfte : [];
  if (r.ok !== true || liste.length === 0) {
    const p = document.createElement("p");
    p.className = "tw-hinweis";
    p.textContent = r.error || "Nichts gefunden.";
    el.appendChild(p);
    return el;
  }

  const grid = document.createElement("div");
  grid.className = "tw-unterkunft-grid";

  for (const u of liste) {
    // Die ganze Karte ist der Link — antippen fuehrt direkt zum Angebot.
    const a = document.createElement("a");
    a.className = "tw-unterkunft";
    if (u.empfehlung) a.classList.add("is-empfehlung");
    if (u.url) {
      a.href = u.url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
    }
    a.title = u.name || "Unterkunft";

    if (u.bild_url) {
      const box = document.createElement("div");
      box.className = "tw-unterkunft-bild";
      const im = document.createElement("img");
      // Ueber den eigenen Proxy — externe Bilder scheitern sonst an
      // Hotlink-Schutz und in der WKWebView (gleiche Lehre wie beim Bild-Grid).
      im.src = proxiedImg(u.bild_url, u.url);
      im.alt = u.name || "";
      im.loading = "lazy";
      im.addEventListener("error", () => box.remove());
      box.appendChild(im);
      a.appendChild(box);
    }

    const txt = document.createElement("div");
    txt.className = "tw-unterkunft-text";

    const kopf = document.createElement("div");
    kopf.className = "tw-unterkunft-kopf";
    const nm = document.createElement("span");
    nm.className = "tw-unterkunft-name";
    nm.textContent = u.name || "Unterkunft";
    kopf.appendChild(nm);
    if (u.empfehlung) {
      const badge = document.createElement("span");
      badge.className = "tw-unterkunft-badge";
      badge.textContent = "Empfehlung";
      kopf.appendChild(badge);
    }
    txt.appendChild(kopf);

    // Zeile mit den harten Zahlen — nur was wirklich da ist.
    const fakten: string[] = [];
    if (typeof u.bewertung === "number") {
      const n =
        typeof u.bewertungen_anzahl === "number"
          ? ` (${u.bewertungen_anzahl})`
          : "";
      fakten.push(`★ ${u.bewertung}${n}`);
    }
    if (u.preis) fakten.push(u.preis);
    if (u.entfernung) fakten.push(u.entfernung);
    if (fakten.length > 0) {
      const z = document.createElement("div");
      z.className = "tw-unterkunft-fakten";
      z.textContent = fakten.join(" · ");
      txt.appendChild(z);
    }

    if (Array.isArray(u.merkmale) && u.merkmale.length > 0) {
      const m = document.createElement("div");
      m.className = "tw-unterkunft-merkmale";
      for (const f of u.merkmale.slice(0, 4)) {
        const chip = document.createElement("span");
        chip.className = "tw-unterkunft-chip";
        chip.textContent = f;
        m.appendChild(chip);
      }
      txt.appendChild(m);
    }

    if (u.hinweis) {
      const h = document.createElement("div");
      h.className = "tw-unterkunft-hinweis";
      h.textContent = u.hinweis;
      txt.appendChild(h);
    }

    a.appendChild(txt);
    grid.appendChild(a);
  }

  el.appendChild(grid);
  return el;
}

export function buildLiveTranslateCard(result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte("live-translate");
  if (r.ok === false) {
    el.appendChild(kartenTitel("Übersetzung — Fehler"));
        el.appendChild(absatz(String(r.error ?? "Unbekannter Fehler"), "warn"));
    return el;
  }
  const target = String(r.target_language ?? "?");
  const source = String(r.source_language ?? "auto");
  el.appendChild(kartenTitel(`Übersetzung · ${source} → ${target}`));

  const src = document.createElement("p");
  src.className = "tool-translate-source";
  src.textContent = String(r.source_text ?? "");
  el.appendChild(src);

  const arrow = document.createElement("div");
  arrow.className = "tool-translate-arrow";
  arrow.textContent = "→";
  el.appendChild(arrow);

  const out = document.createElement("p");
  out.className = "tool-translate-target";
  out.textContent = String(r.translation ?? "");
  el.appendChild(out);
  return el;
}

export function buildFlightSearchCard(result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte("flight-search");
  if (r.ok === false) {
    el.appendChild(kartenTitel("Flugsuche — Fehler"));
        el.appendChild(absatz(String(r.error ?? "Unbekannter Fehler"), "warn"));
    return el;
  }
  const req = r.request as
    | {
        from?: string;
        to?: string;
        departure_date?: string;
        return_date?: string | null;
      }
    | undefined;
  el.appendChild(
    kartenTitel(
      `Flüge ${req?.from ?? "?"} → ${req?.to ?? "?"}`,
    ),
  );
  if (req?.departure_date) {
    wertZeile(
      el,
      "Datum",
      `${req.departure_date}${req.return_date ? ` → ${req.return_date}` : ""}`,
    );
  }
  if (typeof r.count_total === "number") {
    wertZeile(el, "Treffer", String(r.count_total));
  }
  const offers = Array.isArray(r.offers) ? r.offers : [];
  if (offers.length === 0) {
    const url = typeof r.booking_url === "string" ? r.booking_url : null;
    if (url) {
            el.appendChild(absatz("Aktuelle Preise und Zeiten — ein Tipp öffnet die Liste."));
      aktionsLeiste(el).appendChild(aktionsKnopf({ text: "Preise öffnen", primaer: true, href: url, neuesFenster: true }));
      return el;
    }
    leerHinweis(el, "Keine Treffer. Versuch andere Daten oder andere Klasse.");
    return el;
  }
  const list = document.createElement("ul");
  list.className = "tool-flight-list";
  for (const offer of offers) {
    // 2026-07-07 (Ticket 283): null/undefined-Eintraege warfen "undefined
    // is not an object" im WKWebView und killten die Sprachnachricht-Anzeige.
    if (!offer || typeof offer !== "object") continue;
    list.appendChild(buildFlightOfferItem(offer as Record<string, unknown>));
  }
  el.appendChild(list);
  return el;
}

// Pre.383: Schöneres Layout pro Offer. Statt nur IATA-Codes ausgeschriebene
// Städte, Airline-Logo wenn verfügbar, klare Trennung Preis / Route / Zeit.
function buildFlightOfferItem(o: Record<string, unknown>): HTMLLIElement {
  const li = document.createElement("li");
  li.className = "tool-flight-item";

  const bookingUrl = typeof o.booking_url === "string" ? o.booking_url : null;
  if (bookingUrl) {
    li.classList.add("tool-flight-item--clickable");
    li.addEventListener("click", () => {
      window.open(bookingUrl, "_blank", "noopener,noreferrer");
    });
    li.setAttribute("role", "link");
    li.setAttribute("tabindex", "0");
    // 2026-07-28 (Martin: -es oeffnet sich nur Google, aber nicht die Seite,
    // wo ich guenstig buchen kann-): Der Titel versprach Google Flights, und
    // dorthin fuehrte der Link auch — auf eine leere SUCHE. Jetzt geht es zur
    // nach Preis sortierten Ergebnisliste dieser Strecke mit diesen Daten.
    li.title = "Angebote zu diesem Flug ansehen und buchen";
  }

  // --- Header: Preis + Airline ---
  const header = document.createElement("div");
  header.className = "tool-flight-header";

  const left = document.createElement("div");
  left.className = "tool-flight-airline-row";
  const logoUrl = typeof o.airline_logo === "string" ? o.airline_logo : null;
  if (logoUrl) {
    const img = document.createElement("img");
    img.className = "tool-flight-airline-logo";
    img.src = logoUrl;
    img.alt = "";
    img.loading = "lazy";
    img.addEventListener("error", () => img.remove());
    left.appendChild(img);
  }
  const airline = document.createElement("span");
  airline.className = "tool-flight-airline-name";
  airline.textContent = String(o.airline ?? "?");
  left.appendChild(airline);
  header.appendChild(left);

  const price = document.createElement("strong");
  price.className = "tool-flight-price";
  const amount =
    typeof o.price === "number" ? o.price.toFixed(0) : String(o.price ?? "?");
  price.textContent = `${amount} ${String(o.currency ?? "EUR")}`;
  header.appendChild(price);

  li.appendChild(header);

  // --- Slices: Route + Times ---
  const slices = Array.isArray(o.slices) ? o.slices : [];
  for (const s of slices) {
    const sl = s as Record<string, unknown>;
    const dep = sl.departure ? new Date(String(sl.departure)) : null;
    const arr = sl.arrival ? new Date(String(sl.arrival)) : null;

    // 2026-07-09 (Martin, "kompakter + sauberer"): Zwei ruhige Zeilen je Flug
    // statt 3-Spalten-Grid (das quetschte die Stopp-Pille ueber den Zielort).
    const fmtTime = (d: Date | null): string =>
      d
        ? d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
        : "?";
    const fmtDate = (d: Date | null): string =>
      d
        ? d.toLocaleDateString("de-DE", { day: "2-digit", month: "short" })
        : "";

    const leg = document.createElement("div");
    leg.className = "tool-flight-leg";

    const legRoute = document.createElement("div");
    legRoute.className = "tool-flight-legroute";
    const fromName = String(sl.from_city ?? sl.from ?? "?");
    const toName = String(sl.to_city ?? sl.to ?? "?");
    const fromCode = String(sl.from ?? "");
    const toCode = String(sl.to ?? "");
    legRoute.textContent =
      `${fromName}${fromCode ? ` (${fromCode})` : ""} → ` +
      `${toName}${toCode ? ` (${toCode})` : ""}`;
    leg.appendChild(legRoute);

    const legMeta = document.createElement("div");
    legMeta.className = "tool-flight-legmeta";
    const stops = Number(sl.stops ?? 0);
    const stopsTxt =
      stops === 0 ? "Direkt" : stops === 1 ? "1 Stopp" : `${stops} Stopps`;
    const dur = String(sl.duration ?? "").trim();
    const when = dep ? `${fmtDate(dep)} ${fmtTime(dep)}–${fmtTime(arr)}` : "";
    legMeta.textContent = [dur, stopsTxt, when].filter(Boolean).join(" · ");
    leg.appendChild(legMeta);

    li.appendChild(leg);
  }

  return li;
}
